import arrowRight from '@fortawesome/fontawesome-free/svgs/solid/arrow-right.svg';

/**
 * Frontend runtime for the Business Index block.
 *
 * The PHP template renders the first page of results and exposes the selected
 * route/filter context through data attributes. This script then owns the
 * browser-only behavior: alphabet filtering, paginated REST requests, infinite
 * loading with IntersectionObserver, sticky alphabet state, and client-side
 * rendering for results fetched after the initial PHP response.
 *
 * Keep the generated card markup, route URL shape, alias-aware area-name logic,
 * and schema.org attributes aligned with frontend.template.php. Mismatches here
 * can make the first server-rendered cards differ from cards loaded by JS.
 */
document.addEventListener('DOMContentLoaded', function () {
    const contextElements = document.querySelectorAll('.wp-block-jims-wp-business-index');
    if (contextElements.length === 0) {
        return;
    }

    // Each index block owns its own filters, request state, and observers.
    contextElements.forEach(initBusinessIndex);

    function constructBusinessFindUrl(ctx) {
        // Reuse the server-provided REST endpoint and filters so JS results match the initial PHP render.
        // The data attributes are the contract between frontend.template.php and this runtime.
        const url = new URL(ctx.baseUrl);
        const params = {
            state: ctx.state,
            region: ctx.region,
            suburb: ctx.suburb,
            postcode: ctx.postcode,
            starting_letter: ctx.startingLetter.toLowerCase(),
            area_name_field: ctx.areaNameField,
            per_page: ctx.perPage,
            page: ctx.page,
        };

        if (ctx.beAliasAware === true) {
            params.alias_aware_area_name = '1';
        }

        url.search = new URLSearchParams(params).toString();

        return url.toString();
    }

    function ucWords(str) {
        // Mirror the PHP title casing enough for fetched cards to match server-rendered cards.
        return (str ?? '').toLowerCase().replace(/(^([a-zA-Z\p{M}]))|([ -][a-zA-Z\p{M}])/g,
            function (s) {
                return s.toUpperCase();
            });
    }

    function initBusinessIndex(contextElement) {
        // Keep all mutable runtime state scoped to this block instance.
        // Shared state would make filters and pagination collide when a page contains multiple indexes.
        const ctx = {
            baseUrl: contextElement.getAttribute('data-base-url') ?? '',
            siteUrl: contextElement.getAttribute('data-site-url') ?? '',
            state: contextElement.getAttribute('data-state') ?? '',
            region: contextElement.getAttribute('data-region') ?? '',
            suburb: contextElement.getAttribute('data-suburb') ?? '',
            postcode: contextElement.getAttribute('data-postcode') ?? '',
            startingLetter: contextElement.getAttribute('data-starting-letter') ?? 'a',
            areaNameField: contextElement.getAttribute('data-area-name-field') ?? '',
            beAliasAware: contextElement.getAttribute('data-be-alias-aware') !== '0',
            perPage: contextElement.getAttribute('data-per-page') ?? '18',
            page: 1,
            resultsElement: contextElement.querySelector(':scope > ul'),
            noResultsElement: contextElement.querySelector(':scope > .no-results'),
            errorElement: contextElement.querySelector(':scope > .error'),
            intersectionObserver: null,
            isFetching: false,
        };

        // The PHP template must provide these state containers; without them the block cannot update safely.
        if (ctx.resultsElement == null || ctx.noResultsElement == null || ctx.errorElement == null) {
            return;
        }

        // Infinite loading advances the page only when the current sentinel card enters the viewport.
        // fetchDataAndRender() guards repeated observer callbacks while a request is already active.
        ctx.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    ctx.page++;
                    fetchDataAndRender(ctx, true);
                }
            });
        });

        // The last rendered card acts as the infinite-load sentinel.
        observeLastResult(ctx);

        const alphabetLetters = contextElement.querySelectorAll(':scope .alphabet span');
        alphabetLetters.forEach(function (letter) {
            letter.addEventListener('click', event => clickAlphabetLetter(event, ctx, alphabetLetters), false);
        });

        // The pinned class is a visual state only; the alphabet bar remains in normal document flow.
        const alphabetMenuBar = contextElement.querySelector(':scope .alphabet');
        if (alphabetMenuBar != null) {
            const alphabetMenuBarObserver = new IntersectionObserver(
                ([entry]) => {
                    entry.target.classList.toggle('is-pinned', entry.isIntersecting);
                },
                {
                    rootMargin: '-0.01% 0% -99.99% 0%',
                    threshold: 0,
                },
            );
            alphabetMenuBarObserver.observe(alphabetMenuBar);
        }
    }

    function observeLastResult(ctx) {
        // Only the current last card is observed. renderBusinesses() disconnects and replaces this sentinel after each fetch.
        const lastElement = ctx.resultsElement.querySelector(':scope > li:last-child');
        if (lastElement != null) {
            ctx.intersectionObserver.observe(lastElement);
        }
    }

    function isValidBusinessAlias(value) {
        return typeof value === 'string' && value.trim().length > 3;
    }

    function getConfiguredFranchiseAreaName(franchise, areaNameField) {
        // This mirrors the PHP template's area-name field setting so server and JS cards expose the same visible label.
        switch (areaNameField) {
            case 'custom_region_name':
                return franchise.business_area?.custom_region_name ?? '';
            case 'region.name':
                return franchise.region?.name ?? '';
            default:
                return ucWords(franchise.business_area?.name ?? '');
        }
    }

    function getFranchiseAreaName(franchise, areaNameField, beAliasAware) {
        const businessAlias = franchise.business_area?.alias_name ?? franchise.business_alias_name;

        // Business aliases are local SEO/search synonyms. When valid, they become the label and sort key for this block.
        if (beAliasAware && isValidBusinessAlias(businessAlias)) {
            return ucWords(businessAlias);
        }

        return getConfiguredFranchiseAreaName(franchise, areaNameField);
    }

    function escapeHtml(value) {
        // Template strings are used for readability, so API values must be escaped before becoming HTML.
        return String(value ?? '').replace(/[&<>"']/g, (character) => {
            return {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                '\'': '&#039;',
            }[character];
        });
    }

    function getBusinessCardTemplate(card) {
        // API-provided values are escaped before template insertion so fetched cards keep the same safety contract as DOM text setters.
        // Define variables only where values are reused or need escaping before interpolation.
        const franchiseUrl = escapeHtml(card.franchiseUrl);
        const franchiseName = escapeHtml(card.franchiseName);
        const franchiseAreaName = escapeHtml(card.franchiseAreaName);
        const areaNameField = escapeHtml(card.areaNameField);
        const handlerPhoto = escapeHtml(card.handlerPhoto);
        const position = escapeHtml(card.position);
        const arrowIcon = escapeHtml(arrowRight);

        return `
            <li itemprop="itemListElement" itemscope itemtype="https://schema.org/ListItem">
                <meta itemprop="position" content="${position}"/>
                <a itemprop="item" itemscope itemtype="https://schema.org/LocalBusiness" href="${franchiseUrl}" title="Navigate to business ${franchiseName} in ${franchiseAreaName}">
                    <meta itemprop="url" content="${franchiseUrl}"/>
                    <meta itemprop="areaServed" content="${franchiseAreaName}"/>
                    <img itemprop="image" src="${handlerPhoto}" alt="Photo profile of ${franchiseName} in ${franchiseAreaName}"/>
                    <div>
                        <h3 itemprop="name">${areaNameField}</h3>
                        <i class="fa-svg is-arrow-right" aria-hidden="true">
                            <span style="mask-image: url('${arrowIcon}'); -webkit-mask-image: url('${arrowIcon}');"></span>
                        </i>
                    </div>
                </a>
            </li>
        `;
    }

    function renderBusinesses(response, ctx, append) {
        // Unexpected response shapes should show the error state without clearing existing cards.
        if (!response?.result?.data) {
            showError(ctx);
            return;
        }

        ctx.errorElement.classList.remove('visible');

        const franchises = [...(response.result.data ?? [])].sort((a, b) => {
            return getFranchiseAreaName(a, ctx.areaNameField, ctx.beAliasAware)
                .localeCompare(getFranchiseAreaName(b, ctx.areaNameField, ctx.beAliasAware));
        });
        let lastElement;

        if (!append) {
            ctx.resultsElement.replaceChildren();
        }

        // Sort and render fetched records client-side using the same alias-aware label rules as the initial PHP result.
        for (let franchise of franchises) {
            const franchiseAreaName = getFranchiseAreaName(franchise, ctx.areaNameField, ctx.beAliasAware);
            const franchiseName = ucWords(franchise.handler?.name ?? '');
            const areaNameField = getConfiguredFranchiseAreaName(franchise, ctx.areaNameField);
            const stateCode = (franchise.state?.code ?? '').toLowerCase();
            const franchiseSlug = franchise.business_area?.slug ?? '';
            const handlerPhoto = franchise.handler?.photo?.url ?? '';

            // Skip partial API records rather than appending incomplete cards.
            if (!franchiseAreaName || !franchiseName || !stateCode || !franchiseSlug || !handlerPhoto) {
                continue;
            }

            // Public franchise URLs intentionally use /franchise/{state}/{slug}.
            // Keep this in sync with frontend.template.php, SEOManager, and sitemap providers.
            const franchiseUrl = `${ctx.siteUrl}/franchise/${stateCode}/${franchiseSlug}`;

            ctx.resultsElement.insertAdjacentHTML('beforeend', getBusinessCardTemplate({
                position: ctx.resultsElement.children.length + 1,
                franchiseUrl,
                franchiseName,
                franchiseAreaName,
                areaNameField,
                handlerPhoto,
            }));
            lastElement = ctx.resultsElement.lastElementChild;
        }

        // Empty-state messaging applies only to a fresh letter/filter render, not to later pagination pages.
        if (append === false && ctx.resultsElement.children.length === 0) {
            ctx.noResultsElement.classList.add('visible');
        }
        else {
            ctx.noResultsElement.classList.remove('visible');
        }

        // Replace the previous sentinel after each render so only the newest last card can trigger the next page.
        ctx.intersectionObserver.disconnect();
        if (lastElement != null && response.result.meta?.has_more_pages === true) {
            ctx.intersectionObserver.observe(lastElement);
        }

        ctx.isFetching = false;
    }

    function showError(ctx) {
        ctx.errorElement.classList.add('visible');
        ctx.isFetching = false;
    }

    async function fetchDataAndRender(ctx, append) {
        // IntersectionObserver can fire repeatedly while the sentinel is visible; keep one request active.
        if (ctx.isFetching === true) {
            return;
        }

        ctx.isFetching = true;

        try {
            const response = await fetch(constructBusinessFindUrl(ctx));
            if (!response.ok) {
                throw new Error(`Business index request failed: ${response.status}`);
            }

            const responseData = await response.json();
            renderBusinesses(responseData, ctx, append);
        }
        catch (error) {
            console.error(error);
            showError(ctx);
        }
    }

    function clickAlphabetLetter(event, ctx, alphabetLetters) {
        // Changing letters resets pagination and replaces the result list with the first page for the selected letter.
        alphabetLetters.forEach(letter => letter.classList.remove('active'));

        ctx.startingLetter = event.target.getAttribute('data-value') ?? 'A';
        ctx.page = 1;
        event.target.classList.add('active');

        // Preserve the previous scroll-position behavior when changing letters.
        document.body.scrollTop = ctx.resultsElement.scrollTop;

        fetchDataAndRender(ctx, false);
    }
});
